import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {postChatPhoto, postManagerNews} from '../../../api';
import * as colors from '../../../util/colors';
import Paper from '../../common/Paper';
import Button from '../../common/Button';
import LoadingSpinner from '../../common/LoadingSpinner';
import {ErrorAlert} from '../../common/Alerts';

const CHAT_NEWS = 'news';
const PHOTO_MAX_WIDTH = 275;
const PHOTO_MAX_HEIGHT = 390;
const PHOTO_MAX_HORIZONTAL_RATIO = PHOTO_MAX_WIDTH / PHOTO_MAX_HEIGHT;

const getErrorMessage = async (response, fallback) => {
  try {
    const data = await response.json();
    return data.error || fallback;
  } catch (e) {
    return fallback;
  }
};

const calcPhotoHeight = (photo) => {
  if (!photo?.width || !photo?.height) {
    return null;
  }

  const ratio = photo.width / photo.height;

  if (ratio > PHOTO_MAX_HORIZONTAL_RATIO) {
    const width = photo.width > PHOTO_MAX_WIDTH ? PHOTO_MAX_WIDTH : photo.width;
    return Math.round(width / ratio);
  }

  return Math.round(photo.height > PHOTO_MAX_HEIGHT ? PHOTO_MAX_HEIGHT : photo.height);
};

const getImageMeta = file => new Promise((resolve) => {
  if (!file) {
    resolve(null);
    return;
  }

  const objectUrl = URL.createObjectURL(file);
  const image = new Image();

  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve({
      width: image.naturalWidth || image.width || null,
      height: image.naturalHeight || image.height || null,
    });
  };

  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(null);
  };

  image.src = objectUrl;
});

function NewsletterTab({authorization}) {
  const fileInputRef = React.useRef(null);

  const [title, setTitle] = React.useState('');
  const [text, setText] = React.useState('');
  const [file, setFile] = React.useState(null);
  const [previewUrl, setPreviewUrl] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');

  React.useEffect(() => () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  }, [previewUrl]);

  const clearSelectedFile = React.useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setFile(null);
    setPreviewUrl('');
  }, [previewUrl]);

  const handleFileChange = React.useCallback((event) => {
    const selectedFile = event.target.files && event.target.files[0];

    if (!selectedFile) {
      clearSelectedFile();
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setFile(selectedFile);
    setPreviewUrl(URL.createObjectURL(selectedFile));
    setError('');
    setSuccess('');
  }, [clearSelectedFile, previewUrl]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const normalizedTitle = title.trim() || null;
    const normalizedText = text.trim();

    if (!normalizedText) {
      setError('Введите текст новости');
      setSuccess('');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      let attachments = null;

      if (file) {
        const formData = new FormData();
        formData.append('file', file);

        const uploadResponse = await postChatPhoto(CHAT_NEWS, {authorization}, formData);

        if ((uploadResponse.status < 200) || (uploadResponse.status >= 300)) {
          throw new Error(await getErrorMessage(uploadResponse, 'Не удалось загрузить изображение'));
        }

        const uploadData = await uploadResponse.json();
        const imageMeta = await getImageMeta(file);

        attachments = [{
          image: uploadData.url,
          preview: uploadData.url,
          height: calcPhotoHeight(imageMeta),
        }];
      }

      const newsResponse = await postManagerNews({authorization}, {
        title: normalizedTitle,
        text: normalizedText,
        attachments,
      });

      if ((newsResponse.status < 200) || (newsResponse.status >= 300)) {
        throw new Error(await getErrorMessage(newsResponse, 'Не удалось отправить новость'));
      }

      setTitle('');
      setText('');
      clearSelectedFile();
      setSuccess('Новость отправлена в канал news');
    } catch (submitError) {
      setError(submitError.message || 'Не удалось отправить новость');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper>
      {submitting && <LoadingSpinner size={0.8} />}

      <Form onSubmit={handleSubmit}>
        <Heading>Рассылка новостей</Heading>
        <Description>
          Будет создано сообщение в канале <Code>news</Code>. Если прикреплено изображение,
          сначала публикуется запись с attachment, затем текст новости.
        </Description>

        {error && (
          <AlertWrap>
            <ErrorAlert noTriangle>{error}</ErrorAlert>
          </AlertWrap>
        )}

        {success && <SuccessAlert>{success}</SuccessAlert>}

        <FieldGroup>
          <FieldLabel htmlFor="newsletter-title">Заголовок новости</FieldLabel>
          <FieldInput
            id="newsletter-title"
            type="text"
            value={title}
            maxLength={255}
            placeholder="Необязательно"
            onChange={(event) => {
              setTitle(event.target.value);
              setError('');
              setSuccess('');
            }}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="newsletter-text">Текст новости</FieldLabel>
          <FieldTextarea
            id="newsletter-text"
            value={text}
            rows={8}
            placeholder="Введите текст новости"
            onChange={(event) => {
              setText(event.target.value);
              setError('');
              setSuccess('');
            }}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="newsletter-image">Картинка</FieldLabel>
          <UploadRow>
            <FileInput
              ref={fileInputRef}
              id="newsletter-image"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
            <FileInputLabel htmlFor="newsletter-image">
              {file ? 'Заменить изображение' : 'Выбрать изображение'}
            </FileInputLabel>
            {file && (
              <Button
                type="button"
                theme="white"
                minWidth="140px"
                maxWidth="140px"
                margin="0"
                onClick={clearSelectedFile}>
                Убрать
              </Button>
            )}
          </UploadRow>
          <FileMeta>
            {file ? file.name : ''}
          </FileMeta>
          {previewUrl && <PreviewImage src={previewUrl} alt="Предпросмотр новости" />}
        </FieldGroup>

        <ButtonsRow>
          <Button
            type="submit"
            theme="blue"
            minWidth="180px"
            maxWidth="180px"
            margin="0"
            disabled={submitting || !text.trim()}>
            Отправить новость
          </Button>
        </ButtonsRow>
      </Form>
    </Paper>
  );
}

const Form = styled.form`
  position: relative;
`;

const Heading = styled.h2`
  margin: 0 0 8px;
  font-size: 20px;
  line-height: 1.2;
  color: ${colors.TEXT_TITLE};
`;

const Description = styled.div`
  margin-bottom: 24px;
  color: ${colors.TEXT_MINOR};
  font-size: 14px;
  line-height: 1.6;
`;

const Code = styled.code`
  font-size: 13px;
  color: ${colors.TEXT_MAIN};
  background: ${colors.BG};
  padding: 2px 6px;
  border-radius: 4px;
`;

const AlertWrap = styled.div`
  margin-bottom: 16px;
`;

const SuccessAlert = styled.div`
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 5px;
  background: #eef8e7;
  color: ${colors.GREEN_DARK};
  font-weight: bold;
`;

const FieldGroup = styled.div`
  margin-bottom: 20px;
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: ${colors.TEXT_MINOR};
  font-size: 14px;
`;

const fieldStyles = `
  width: 100%;
  border: 1px solid ${colors.INPUT_STROKE};
  border-radius: 5px;
  background: white;
  color: ${colors.TEXT_MAIN};
  font-size: 14px;
  line-height: 1.5;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${colors.BLUE_MAIN};
    box-shadow: 0 0 0 3px rgba(46, 131, 255, 0.08);
  }
`;

const FieldInput = styled.input`
  ${fieldStyles}
  min-height: 44px;
  padding: 0 14px;
`;

const FieldTextarea = styled.textarea`
  ${fieldStyles}
  resize: vertical;
  min-height: 180px;
  padding: 12px 14px;
`;

const UploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 220px;
  max-width: 220px;
  min-height: 40px;
  max-height: 40px;
  padding: 0 26px;
  border: 1px solid ${colors.BUTTON_BLUE};
  border-radius: 5px;
  background: white;
  color: ${colors.BUTTON_BLUE};
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  box-sizing: border-box;
`;

const FileMeta = styled.div`
  margin-top: 10px;
  color: ${colors.TEXT_MINOR};
  font-size: 13px;
  line-height: 1.5;
`;

const PreviewImage = styled.img`
  display: block;
  width: 100%;
  max-width: 420px;
  margin-top: 16px;
  border-radius: 8px;
  border: 1px solid ${colors.LINE};
`;

const ButtonsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 28px;
`;

NewsletterTab.propTypes = {
  authorization: PropTypes.string,
};

NewsletterTab.defaultProps = {
  authorization: '',
};

export default NewsletterTab;
